import React, { useEffect, useRef, useState } from "react";
import {
  AlertCircle, TriangleAlert, Mail, Activity, CreditCard, Send, Database, Webhook, ChevronRight, History,
  RefreshCw, CheckCircle2, XCircle, Gift, User, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import StripeDiagnosticsPanel from "./StripeDiagnosticsPanel";
import {
  Kpi, SectionCard, LinkAction, EmptyState, Spinner, Th, Pill, Avatar, DeliveryPill, PriorityPill, StateDot,
  formatEUR, formatDateTime, relTime, PLAN_LABEL,
} from "./adminUi";

const CASE_ICON = { payment: CreditCard, email: Mail, webhook: Webhook, customer: User, system: TriangleAlert };
const CASE_LABEL = { payment: "Pagamento", email: "Email", webhook: "Webhook", customer: "Cliente", system: "Sistema" };
const ACTIVITY_ICON = {
  payment_paid: CheckCircle2, renewal_paid: RefreshCw, payment_failed: XCircle, manual_grant: Gift,
  email_retry: Mail, webhook_failed: Webhook, admin_action: Activity,
};

export default function AdminProblems({ period, focus, goTo, onOpenUser, onRetryEmail, refreshKey }) {
  const [cc, setCc] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState("");
  const [bump, setBump] = useState(0);
  const refs = { payments: useRef(null), email: useRef(null), stripe: useRef(null), activity: useRef(null) };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([api.get(`/admin/control-center?days=${period}`), api.get("/admin/audit-log")])
      .then(([c, a]) => { if (!alive) return; setCc(c.data); setAudit(a.data?.events || []); })
      .catch((err) => toast.error(err.response?.data?.detail || err.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period, refreshKey, bump]);

  useEffect(() => {
    if (!cc || !focus || !refs[focus]?.current) return;
    refs[focus].current.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cc, focus]);

  const retry = async (kind, invoiceId) => {
    setRetrying(`${kind}:${invoiceId}`);
    try { await onRetryEmail(kind, invoiceId); setBump((b) => b + 1); }
    finally { setRetrying(""); }
  };

  if (loading && !cc) return <Spinner />;
  const k = cc?.kpis || {};
  const sys = cc?.system || {};
  const allOk = ["stripe","webhook","resend","database"].every((s) => sys[s]?.state === "ok");
  const payments = cc?.payments_to_handle || [];
  const failedUserIds = new Set(payments.map((p) => p.user_id).filter(Boolean));
  const pastDue = (cc?.past_due_users || []).filter((u) => !failedUserIds.has(u.id));
  const emails = cc?.email_problems || [];
  const wh = cc?.webhooks || {};
  const cases = cc?.open_cases || [];

  return (
    <div className="space-y-6" data-testid="admin-problems">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <Kpi icon={AlertCircle} label="Pagamenti falliti" value={k.failed_payments?.count ?? 0} sub={`${k.failed_payments?.period ?? 0} negli ultimi ${period} giorni`} tone="fail" testid="prob-kpi-failed-payments" onClick={() => goTo("payments", { status: "failed" })} />
        <Kpi icon={TriangleAlert} label="Webhook Stripe falliti" value={k.failed_webhooks?.count ?? 0} sub="in attesa retry Stripe" tone={k.failed_webhooks?.count ? "fail" : "neutral"} testid="prob-kpi-failed-webhooks" />
        <Kpi icon={Mail} label="Email non consegnate" value={k.email_problems?.count ?? 0} sub="failed · bounced · no recipient" tone={k.email_problems?.count ? "fail" : "neutral"} testid="prob-kpi-emails" />
        <Kpi icon={User} label="Account past_due" value={k.past_due_users?.count ?? 0} sub="rinnovo non riuscito" tone={k.past_due_users?.count ? "warn" : "neutral"} testid="prob-kpi-pastdue" onClick={() => goTo("subscribers", { billing_status: "past_due" })} />
        <Kpi icon={ShieldCheck} label="Sistema" value={<span className={allOk ? "text-emerald-700" : "text-amber-800"}>{allOk ? "Operativo" : "Attenzione"}</span>} sub={allOk ? "tutti i servizi configurati" : "verifica stato servizi"} tone={allOk ? "ok" : "warn"} testid="prob-kpi-system" />
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div ref={refs.payments}>
          <SectionCard icon={CreditCard} title="Pagamenti da gestire" action={<LinkAction onClick={() => goTo("payments", { status: "failed" })} testid="prob-see-payments">Vedi tutti</LinkAction>} padded={false} testid="prob-payments" className="h-full">
            {payments.length === 0 && pastDue.length === 0 ? <EmptyState>Nessun pagamento da gestire.</EmptyState> : (
              <ul className="divide-y divide-border">
                {payments.map((p) => (
                  <li key={p.invoice_id} className="px-5 py-3 flex items-center gap-3 hover:bg-neutral-50 cursor-pointer" onClick={() => p.user_id && onOpenUser(p.user_id)} data-testid={`prob-payment-${p.invoice_id}`}>
                    <Avatar name={p.user_name} email={p.user_email} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.user_name || p.user_email || "—"}</div>
                      <div className="text-xs text-neutral-500 truncate">Piano {PLAN_LABEL[p.plan] || "—"} · {relTime(p.failed_at)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium tabular-nums text-rose-700">{formatEUR(p.amount_due)}</div>
                      <div className="text-[11px] text-neutral-500">Pagamento fallito</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                  </li>
                ))}
                {pastDue.map((u) => (
                  <li key={u.id} className="px-5 py-3 flex items-center gap-3 hover:bg-neutral-50 cursor-pointer" onClick={() => onOpenUser(u.id)} data-testid={`prob-pastdue-${u.id}`}>
                    <Avatar name={u.name} email={u.email} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                      <div className="text-xs text-neutral-500 truncate">Piano {PLAN_LABEL[u.plan] || "—"} · {u.billing_cycle === "yearly" ? "Annuale" : "Mensile"}</div>
                    </div>
                    <Pill tone="fail">past_due</Pill>
                    <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <div ref={refs.email}>
          <SectionCard icon={Mail} title="Email & Resend" padded={false} testid="prob-emails" className="h-full">
            {emails.length === 0 ? <EmptyState>Nessuna email problematica.</EmptyState> : (
              <ul className="divide-y divide-border">
                {emails.slice(0, 12).map((e) => (
                  <li key={`${e.kind}:${e.invoice_id}`} className="px-5 py-3 flex items-start gap-3" data-testid={`prob-email-${e.invoice_id}`}>
                    <span className="w-8 h-8 rounded-full bg-neutral-100 inline-flex items-center justify-center shrink-0"><Mail className="w-4 h-4 text-neutral-600" /></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <button type="button" className="text-sm font-medium truncate hover:underline text-left" onClick={() => e.user_id && onOpenUser(e.user_id)}>{e.user_name || e.user_email || e.to || "—"}</button>
                        <span className="text-[11px] text-neutral-400 whitespace-nowrap">{relTime(e.last_attempt_at || e.created_at)}</span>
                      </div>
                      <div className="text-xs text-neutral-600 truncate">{e.kind === "accounting" ? "Email amministrativa" : "Ricevuta cliente"} · {e.subject || e.invoice_id}</div>
                      {e.last_error && <div className="text-[11px] text-rose-700 truncate">{e.last_error}</div>}
                      <div className="mt-1.5 flex items-center gap-2">
                        <DeliveryPill status={e.delivery_status && e.delivery_status !== "sent" ? e.delivery_status : e.status} />
                        {e.retryable ? (
                          <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" disabled={retrying === `${e.kind}:${e.invoice_id}`} onClick={() => retry(e.kind, e.invoice_id)} data-testid={`prob-email-retry-${e.invoice_id}`}>
                            {retrying === `${e.kind}:${e.invoice_id}` ? "Invio…" : "Riprova"}
                          </Button>
                        ) : <span className="text-[11px] text-neutral-400">retry non consentito</span>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <div ref={refs.stripe}>
          <SectionCard icon={Activity} title="Stripe & Sistema" testid="prob-system" className="h-full">
            <div className="space-y-5">
              <div>
                <div className="label-uppercase text-neutral-600 mb-2 flex items-center gap-1.5"><Webhook className="w-3.5 h-3.5" /> Webhook recenti</div>
                {(wh.recent || []).length === 0 ? <div className="text-xs text-neutral-400">Nessun evento ricevuto.</div> : (
                  <ul className="space-y-1.5">
                    {wh.recent.slice(0, 6).map((w) => (
                      <li key={w.event_id} className="flex items-center gap-2 text-xs">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${w.status === "processed" ? "bg-emerald-500" : w.status === "failed" ? "bg-rose-500" : "bg-amber-500"}`} />
                        <span className="flex-1 truncate">{w.event_type}</span>
                        <span className="text-neutral-400 whitespace-nowrap">{relTime(w.received_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {(wh.failed || []).length > 0 && (
                  <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-3 py-2" data-testid="prob-webhook-failed-note">
                    {wh.failed_total} webhook falliti — Errore webhook — attesa retry Stripe / verifica operatore
                  </div>
                )}
              </div>
              <div>
                <div className="label-uppercase text-neutral-600 mb-2 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Stato servizi</div>
                <div className="divide-y divide-border">
                  {[
                    [CreditCard, "Stripe", sys.stripe?.state, sys.stripe?.mode ? `modalità ${sys.stripe.mode}` : "chiave mancante"],
                    [Webhook, "Webhook Stripe", sys.webhook?.state, sys.webhook?.state === "fail" ? "secret mancante" : `${sys.webhook?.received_period ?? 0} eventi nel periodo`],
                    [Send, "Resend", sys.resend?.state, sys.resend?.state === "ok" ? (sys.resend?.webhook_secret_present ? "webhook firmato" : "webhook non firmato") : "non configurato"],
                    [Database, "Database", sys.database?.state, "ping"],
                    [Mail, "Fatturazione email", sys.billing_email?.state, sys.billing_email?.problem_count ? `${sys.billing_email.problem_count} da gestire` : "nessun problema"],
                  ].map(([Icon, name, state, sub]) => (
                    <div key={name} className="py-2 flex items-center gap-2 text-sm">
                      <Icon className="w-3.5 h-3.5 text-neutral-500" />
                      <span className="flex-1">{name}</span>
                      <StateDot state={state} label={state === "fail" ? "Errore" : undefined} />
                      <span className="text-[11px] text-neutral-400 w-28 text-right truncate">{sub}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div ref={refs.activity}>
                <div className="label-uppercase text-neutral-600 mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Attività recenti</div>
                {(cc?.recent_activity || []).length === 0 ? <div className="text-xs text-neutral-400">Nessuna attività.</div> : (
                  <ul className="space-y-2">
                    {cc.recent_activity.slice(0, 6).map((a, i) => {
                      const Icon = ACTIVITY_ICON[a.kind] || Activity;
                      return (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${a.kind.includes("failed") ? "text-rose-600" : "text-neutral-500"}`} />
                          <div className="flex-1 min-w-0"><div className="font-medium truncate">{a.title}</div><div className="text-neutral-500 truncate">{a.subtitle}</div></div>
                          <span className="text-neutral-400 whitespace-nowrap">{relTime(a.at)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      <SectionCard icon={TriangleAlert} title="Casi aperti" padded={false} testid="prob-open-cases">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-neutral-50/60"><Th>Tipo</Th><Th>Cliente</Th><Th>Dettaglio</Th><Th>Priorità</Th><Th>Ultimo aggiornamento</Th><Th align="right">Azione</Th></tr></thead>
            <tbody>
              {cases.length === 0 && <tr><td colSpan={6}><EmptyState>Nessuna anomalia aperta. Tutto in ordine.</EmptyState></td></tr>}
              {cases.map((c, i) => {
                const Icon = CASE_ICON[c.type] || TriangleAlert;
                const action = c.type === "payment" ? () => goTo("payments", { status: "failed" })
                  : c.type === "email" ? () => refs.email.current?.scrollIntoView({ behavior: "smooth" })
                  : c.type === "webhook" ? () => refs.stripe.current?.scrollIntoView({ behavior: "smooth" })
                  : c.user_id ? () => onOpenUser(c.user_id) : null;
                return (
                  <tr key={`${c.type}:${c.ref}:${i}`} className="border-b border-border last:border-b-0 hover:bg-neutral-50/80" data-testid={`prob-case-${c.type}-${i}`}>
                    <td className="py-3 px-4"><span className="inline-flex items-center gap-2 text-neutral-700"><Icon className="w-3.5 h-3.5 text-neutral-500" /> {CASE_LABEL[c.type] || c.type}</span></td>
                    <td className="py-3 px-4">{c.user_id ? <button type="button" className="hover:underline text-left" onClick={() => onOpenUser(c.user_id)}>{c.who || "—"}</button> : (c.who || "—")}</td>
                    <td className="py-3 px-4 text-neutral-700">{c.detail}</td>
                    <td className="py-3 px-4"><PriorityPill p={c.priority} /></td>
                    <td className="py-3 px-4 text-neutral-500 whitespace-nowrap">{relTime(c.at)}</td>
                    <td className="py-3 px-4 text-right">{action && <Button size="sm" variant="outline" className="h-8 rounded-md" onClick={action}>Gestisci</Button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <StripeDiagnosticsPanel />

      <SectionCard icon={History} title="Log azioni admin" padded={false} testid="prob-audit-log">
        {audit.length === 0 ? <EmptyState>Nessuna azione registrata.</EmptyState> : (
          <ul className="divide-y divide-border" data-testid="admin-audit-list">
            {audit.slice(0, 30).map((e, i) => (
              <li key={i} className="px-5 py-2.5 text-sm flex justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{e.action}</span>
                  {e.old_value !== null && e.new_value !== null && e.old_value !== undefined && e.new_value !== undefined && (
                    <span className="text-neutral-500 text-xs"> · {String(e.old_value)} → {String(e.new_value)}</span>
                  )}
                  {e.new?.plan && <span className="text-neutral-500 text-xs"> · {PLAN_LABEL[e.new.plan] || e.new.plan}{e.new.duration ? ` · ${e.new.duration}` : ""}</span>}
                  {e.target_user_id && <button type="button" className="text-neutral-400 text-xs ml-1 hover:underline" onClick={() => onOpenUser(e.target_user_id)}>· target {String(e.target_user_id).slice(0, 8)}…</button>}
                </div>
                <div className="text-xs text-neutral-400 tabular-nums">{formatDateTime(e.created_at)}</div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
